if (!customElements.get("product-info")) {
	customElements.define(
		"product-info",
		class ProductInfo extends HTMLElement {
			constructor() {
				super();

				this.abortController = undefined;
				this.onVariantChangeUnsubscriber = undefined;
				this.pendingRequestUrl = null;
				this.preProcessHtmlCallbacks = [];
				this.postProcessHtmlCallbacks = [];
				this.cartUpdateUnsubscriber = undefined;
			}

			get variantSelectors() {
				return this.querySelector("variant-selects");
			}

			get productId() {
				return this.getAttribute("data-product-id");
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section;
			}

			get pickupAvailability() {
				return this.querySelector(`pickup-availability`);
			}

			get productForm() {
				return this.querySelector('form[is="product-form"]');
			}

			get quantityInput() {
				return this.querySelector("quantity-input input");
			}

			connectedCallback() {
				this.initializeProductSwapUtility();

				this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
					FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
					this.handleOptionValueChange.bind(this)
				);

				this.initQuantityHandlers();

				this.currentVariant = this.getSelectedVariant(this);
				if (this.currentVariant) {
					this.updateMedia(this.currentVariant);
					// Show restock notify if not available on first load
					if (this.currentVariant.available === false) {
						this.toggleRestockNotify(true);
					}
				}

				// --- Add event listener for restock notify ---
				this.initRestockNotifyHandler();
			}

			disconnectedCallback() {
				this.onVariantChangeUnsubscriber();
				this.cartUpdateUnsubscriber?.();
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((_newNode) => {
					window?.Shopify?.PaymentButton?.init();
					window?.ProductModel?.loadShopifyXR();
				});
			}

			handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
				if (!this.contains(event.target)) return;
				const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
				const shouldSwapProduct = this.dataset.url !== productUrl;
				const shouldFetchFullPage = this.dataset.updateUrl === "true" && shouldSwapProduct;
				const viewMode = this.dataset.viewMode || "main-product";

				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
					targetId: target.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
						: this.handleUpdateProductInfo(productUrl, viewMode),
				});
			}

			handleSwapProduct(productUrl, updateFullPage, viewMode) {
				return (html) => {
					const quickView = html.querySelector("#MainProduct-quick-view__content");
					if (quickView && viewMode === "quick-view") {
						html = quickView.content.cloneNode(true);
					}
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : "product-info";
					const variant = this.getSelectedVariant(html.querySelector(selector));

					this.updateURL(productUrl, variant?.id);

					if (updateFullPage) {
						document.querySelector("head title").innerHTML = html.querySelector("head title").innerHTML;
						HTMLUpdateUtility.viewTransition(
							document.querySelector("main"),
							html.querySelector("main"),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						);
						HTMLUpdateUtility.viewTransition(
							document.getElementById("shopify-section-sticky-atc-bar"),
							html.getElementById("shopify-section-sticky-atc-bar"),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						);
					} else {
						HTMLUpdateUtility.viewTransition(this, html.querySelector("product-info"), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks);
					}

					this.currentVariant = variant;
				};
			}

			handleUpdateProductInfo(productUrl, viewMode) {
				return (html) => {
					const quickView = html.querySelector("#MainProduct-quick-view__content");
					if (quickView && viewMode === "quick-view") {
						html = quickView.content.cloneNode(true);
					}
					const variant = this.getSelectedVariant(html);

					this.pickupAvailability?.update(variant);
					this.updateOptionValues(html);
					this.updateURL(productUrl, variant?.id);
					this.updateShareUrl(variant?.id);
					this.updateVariantInputs(variant?.id);

					if (!variant) {
						return;
					}

					this.updateMedia(variant);

					// --- Update hidden notify-me input with correct variant id on variant change ---
					const restock = this.querySelector(".restock-notify");
					if (restock) {
						const variantInput = restock.querySelector('input[name="variant_id"]');
						if (variantInput) {
							variantInput.value = variant.id;
						}
					}

					// Reset restock notify UI on every variant change
					this.resetRestockNotifyUI();

					// Toggle visibility only, do not inject HTML
					const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
					const isSoldOut = addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true;
					this.toggleRestockNotify(isSoldOut);

					const updateSourceFromDestination = (id, shouldHide = (_source) => false) => {
						const source = html.getElementById(`${id}-${this.sectionId}`);
						const destination = this.querySelector(`#${id}-${this.dataset.section}`);

						if (source && destination) {
							destination.innerHTML = source.innerHTML;
							destination.classList.toggle("hidden", shouldHide(source));
						}
					};

					updateSourceFromDestination("price");
					updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
					updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");
					updateSourceFromDestination("Badges", ({ classList }) => classList.contains("hidden"));
					updateSourceFromDestination("PricePerItem", ({ classList }) => classList.contains("hidden"));
					updateSourceFromDestination("Volume");

					this.updateQuantityRules(this.sectionId, this.productId, html);
					this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove("hidden");
					this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove("hidden");

					HTMLUpdateUtility.viewTransition(
						document.querySelector(`#SizeChart-${this.sectionId}`),
						html.querySelector(`#SizeChart-${this.sectionId}`),
						this.preProcessHtmlCallbacks,
						this.postProcessHtmlCallbacks
					);

					FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
						data: {
							sectionId: this.sectionId,
							html,
							variant,
						},
					});

					document.dispatchEvent(
						new CustomEvent("variant:changed", {
							detail: {
								variant: variant,
							},
						})
					);
				};
			}

			resetRestockNotifyUI() {
				const restock = this.querySelector(".restock-notify");
				if (!restock) return;
				const btn = restock.querySelector(".restock-notify-btn");
				const input = restock.querySelector('input[type="email"]');
				const feedback = restock.querySelector(".restock-notify-feedback");
				const label = restock.querySelector(".restock-notify__label");

				if (input) {
					input.value = "";
					input.style.display = "";
					input.removeAttribute("aria-invalid");
				}
				if (btn) btn.style.display = "";
				if (label) label.style.display = "";
				if (feedback) {
					feedback.style.display = "none";
					feedback.textContent = "";
				}
			}

			toggleRestockNotify(show) {
				const formButtons = this.querySelector(".product-form__buttons");
				const restock = this.querySelector(".restock-notify");
				const addButton = formButtons?.querySelector('button[name="add"]');
				if (!restock || !addButton) return;
				if (show) {
					restock.style.display = "";
					formButtons.style.display = "none";

					// Set up the Klaviyo integration each time we show the form
					this.setupKlaviyoIntegration();
				} else {
					restock.style.display = "none";
					formButtons.style.display = "";
				}
			}

			setupKlaviyoIntegration() {
				const restock = this.querySelector(".restock-notify");
				if (!restock) return;

				const btn = restock.querySelector(".restock-notify-btn");
				const input = restock.querySelector('input[type="email"]');
				if (!btn || !input) return;

				// Remove existing click handlers to prevent duplicates
				const newBtn = btn.cloneNode(true);
				btn.parentNode.replaceChild(newBtn, btn);

				// Create or get feedback element
				let feedback = restock.querySelector(".restock-notify-feedback");
				if (!feedback) {
					feedback = document.createElement("div");
					feedback.className = "restock-notify-feedback text-sm mt-1";
					feedback.style.display = "none";
					restock.appendChild(feedback);
				}

				// Add the click handler
				newBtn.addEventListener("click", () => {
					const email = input.value.trim();

					// Validate email
					const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
					if (!email || !emailRegex.test(email)) {
						feedback.textContent = FoxTheme.restockNotify.errorMessage;
						feedback.style.color = "#E8083E";
						feedback.style.display = "";
						input.setAttribute("aria-invalid", "true");
						return;
					}

					// Get the variant ID
					const variantInput = restock.querySelector('input[name="variant_id"]');
					const variantId = variantInput ? variantInput.value : null;
					if (!variantId) {
						return;
					}

					// Show loading state
					newBtn.disabled = true;
					newBtn.textContent = "…";

					// Build payload
					const payload = {
						data: {
							type: "back-in-stock-subscription",
							attributes: {
								profile: {
									data: {
										type: "profile",
										attributes: {
											email: email,
										},
									},
								},
								channels: ["EMAIL"],
							},
							relationships: {
								variant: {
									data: {
										type: "catalog-variant",
										id: `$shopify:::$default:::${variantId}`,
									},
								},
							},
						},
					};
					const element = document.querySelector(".restock-notify");
					const companyId = element.getAttribute("data-company-id");

					fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							revision: "2024-06-15",
						},
						body: JSON.stringify(payload),
					})
						.then((result) => {
							newBtn.disabled = false;
							newBtn.textContent = FoxTheme?.restockNotify?.submitButton || "Envoyer";

							if (!result.ok) throw new Error("Network response was not ok");

							// Hide input, button, and label, show success
							input.style.display = "none";
							newBtn.style.display = "none";
							if (restock.querySelector(".restock-notify__label")) {
								restock.querySelector(".restock-notify__label").style.display = "none";
							}

							feedback.innerHTML = FoxTheme.restockNotify.successMessage;
							feedback.style.color = "#1a7f37";
							feedback.style.display = "";


							//make another api call to client/profiles to update the profile with the locale
							fetch(`https://a.klaviyo.com/client/profiles?company_id=${companyId}`, {
								method: "POST",
								headers: {
									"Content-Type": "application/json",
									revision: "2024-06-15",
								},
								body: JSON.stringify({
									data: {
										type: "profile",
										attributes: {
											email: email,
											locale: window.Shopify?.locale || "fr"
										},
									},
								}),
							}).then(result2 => {
								if (!result2.ok) {
									console.log("Failed to update profile with locale", result2.statusText);
								}else{
									console.log("Profile updated with locale successfully", result2.statusText);
								}
							})


						})
						.catch((error) => {
							newBtn.disabled = false;
							newBtn.textContent = FoxTheme?.restockNotify?.submitButton || "Envoyer";

							feedback.innerHTML = FoxTheme.restockNotify.failedMessage;
							feedback.style.color = "#E8083E";
							feedback.style.display = "";
						});
				});
			}

			initRestockNotifyHandler() {
				// This method is for setting up the UI feedback on email input
				const restock = this.querySelector(".restock-notify");
				if (!restock) return;

				const input = restock.querySelector('input[type="email"]');
				if (!input) return;

				// Hide feedback on input
				input.addEventListener("input", () => {
					const feedback = restock.querySelector(".restock-notify-feedback");
					if (feedback) {
						feedback.style.display = "none";
					}
					input.removeAttribute("aria-invalid");
				});

				// Initialize Klaviyo integration if the restock notify is visible
				if (restock.style.display !== "none") {
					this.setupKlaviyoIntegration();
				}
			}

			buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
				const params = [];

				!shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

				if (optionValues.length) {
					params.push(`option_values=${optionValues.join(",")}`);
				}

				return `${url}?${params.join("&")}`;
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector("variant-selects [data-selected-variant]")?.innerHTML;
				return !!selectedVariant ? JSON.parse(selectedVariant) : null;
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				this.abortController?.abort();
				this.abortController = new AbortController();

				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null;
						const html = new DOMParser().parseFromString(responseText, "text/html");
						callback(html);
					})
					.then(() => {
						document.querySelector(`#${targetId}`)?.focus();
					})
					.catch((error) => {
						if (error.name === "AbortError") {
							console.log("Fetch aborted by user");
						} else {
							console.error(error);
						}
					});
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector("variant-selects");
				if (variantSelects) {
					HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === "false") return;
				window.history.replaceState({}, "", `${url}${variantId ? `?variant=${variantId}` : ""}`);
			}

			updateVariantInputs(variantId) {
				document
					.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
					.forEach((productForm) => {
						const input = productForm.querySelector('input[name="id"]');
						input.value = variantId ?? "";
						input.dispatchEvent(new Event("change", { bubbles: true }));
					});
			}

			updateMedia(variant) {
				const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
				if (!productMedia) return;

				const setActiveMedia = () => {
					if (typeof productMedia.setActiveMedia === "function") {
						productMedia.init();
						productMedia.setActiveMedia(variant);
						return true;
					}
					return false;
				};

				if (!setActiveMedia()) {
					this.timer = setInterval(() => {
						if (setActiveMedia()) {
							clearInterval(this.timer);
						}
					}, 100);
				}
			}

			updateShareUrl(variantId) {
				if (!variantId) return;
				const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
				if (!shareButton || !shareButton.updateUrl) return;
				shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
			}

			toggleAddButton(disable = true, text, modifyClass = true) {
				const productForm = document.getElementById(`product-form-${this.dataset.section}`);
				if (!productForm) return;
				const addButton = productForm.querySelector('[name="add"]');
				const addButtonText = productForm.querySelector('[name="add"] > span');
				if (!addButton) return;

				if (disable) {
					addButton.setAttribute("disabled", "disabled");
					if (text) addButtonText.textContent = this.decoded(text);
				} else {
					addButton.removeAttribute("disabled");
					addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
				}

				if (!modifyClass) return;
			}

			decoded(text) {
				const tempDiv = document.createElement("div");
				tempDiv.innerHTML = text;
				const decoded = tempDiv.textContent;
				return decoded;
			}

			setUnavailable() {
				this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
				const price = document.getElementById(`price-${this.dataset.section}`);
				const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
				const sku = document.getElementById(`Sku-${this.dataset.section}`);

				if (price) price.classList.add("hidden");
				if (inventory) inventory.classList.add("hidden");
				if (sku) sku.classList.add("hidden");
			}

			initQuantityHandlers() {
				if (!this.quantityInput) return;

				this.setQuantityBoundries();
				if (!this.hasAttribute("data-original-section")) {
					this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
				}
			}

			setQuantityBoundries() {
				FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
					data: {
						sectionId: this.sectionId,
						productId: this.productId,
					},
				});
			}

			fetchQuantityRules() {
				const currentVariantId = this.productForm?.productIdInput?.value;
				if (!currentVariantId) return;

				this.querySelector(".quantity__rules-cart")?.classList.add("btn--loading");

				fetch(`${this.getAttribute("data-url")}?variant=${currentVariantId}&section_id=${this.sectionId}`)
					.then((response) => response.text())
					.then((responseText) => {
						const parsedHTML = new DOMParser().parseFromString(responseText, "text/html");
						this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
					})
					.catch((error) => {
						console.error(error);
					})
					.finally(() => {
						this.querySelector(".quantity__rules-cart")?.classList.remove("btn--loading");
					});
			}

			updateQuantityRules(sectionId, productId, parsedHTML) {
				if (!this.quantityInput) return;

				FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
					data: {
						sectionId,
						productId,
						parsedHTML,
					},
				});

				this.setQuantityBoundries();
			}
		}
	);
}
